<!-- src/components/PopupLayer.vue -->
<template>
  <Teleport to="body">
    <div v-if="visiblePopups.length" class="backdrop" @click.self="closeAll" @keydown.esc="closeAll" tabindex="-1">
      <PopupWindow
        v-for="(p, i) in visiblePopups"
        :key="p.id || p.storageKey || i"
        :popup="p"
        :apiBaseUrl="apiBaseUrl"
        :initial-x="positions[i]?.x || 40"
        :initial-y="positions[i]?.y || 60"
        :z-index="zMap.get(p.storageKey) || baseZ + i"
        @close="closeOne"
        @focus="focusOne"
      />
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onBeforeUnmount, reactive, ref, watchEffect } from 'vue'
import PopupWindow from './PopupWindow.vue'

const props = defineProps({
  popups: { type: Array, required: true },     // MainHome의 popupList
  apiBaseUrl: { type: String, default: '' },
})

const emit = defineEmits(['update:popups', 'close-popup'])

const baseZ = 20000
const zCounter = ref(baseZ)
const zMap = reactive(new Map())

const visiblePopups = computed(() => props.popups.filter(p => p.show))
const vw = ref(window.innerWidth)
const vh = ref(window.innerHeight)

function onResize() {
  vw.value = window.innerWidth
  vh.value = window.innerHeight
}
window.addEventListener('resize', onResize, { passive: true })
onBeforeUnmount(() => window.removeEventListener('resize', onResize))

const positions = computed(() => {
  // 팝업 예상 크기(겹침 방지용)
  const POPUP_W = Math.min(500, vw.value * 0.95)
  const POPUP_H = 520 // 넉넉히(이미지/푸터 포함) 잡기
  const GAP = 2
  const PADDING = 2

  const availW = Math.max(0, vw.value - PADDING * 2)
  const availH = Math.max(0, vh.value - PADDING * 2)

  // 가로로 놓을 수 있는 개수
  const cols = Math.max(1, Math.floor((availW + GAP) / (POPUP_W + GAP)))

  return visiblePopups.value.map((_, idx) => {
    const col = idx % cols
    const row = Math.floor(idx / cols)

    let x = PADDING + col * (POPUP_W + GAP)
    let y = PADDING + row * (POPUP_H + GAP)

    // 화면 아래로 넘어가면 스크롤이 필요하므로 y는 그대로 두되,
    // “무조건 화면 안” 정책이면 여기서 clamp 가능(그럼 겹침이 다시 생김)
    return { x, y }
  })
})

function closeOne(popup) {
  emit('close-popup', popup)
}

function closeAll() {
  visiblePopups.value.forEach(p => emit('close-popup', p))
}

function focusOne(popup) {
  zCounter.value += 1
  zMap.set(popup.storageKey, zCounter.value)
}

watchEffect(() => {
  // 새로 열린 팝업은 z-index 초기 등록
  visiblePopups.value.forEach((p, i) => {
    if (!zMap.has(p.storageKey)) zMap.set(p.storageKey, baseZ + i)
  })
})
</script>

<style scoped>
.backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, .55);
  z-index: 9999;
  /* 추가 */
  overflow: auto;
  padding: 24px;
}
</style>
